import { useMemo } from 'react'
import { isEmpty } from 'ramda'

import { OrgByFunds, OrgByFundsTotals } from 'app/dataTypes/reports/data/common/ordersByClasses'
import {
  CompositeTableTemplate,
  renderCellGrayZeros,
} from 'app/common/templates-next/table-template'

import {
  ColumnKey,
  columnKeysLeft,
  createColumnKeysMain,
  createColumnKeysMainHeaders,
  createHeaderCells,
  createDinamicColumnKey,
  createColumnKeysLeftHeaders,
  createHeaderCellsProps,
  createCellsProps,
  createTotalCellProps,
} from './table-columns'

import { headerCell } from './header-cell'

type Props = {
  allowanceIDs: Array<string>
  data: Array<OrgByFunds>
  totals: OrgByFundsTotals
  pending?: boolean
}

export const OrgsGrouppedByFunds = ({ allowanceIDs, data, totals, pending = false }: Props) => {
  const tableData = useMemo(() => {
    const hasAllowances = !isEmpty(allowanceIDs)
    return {
      columnKeysLeft,
      columnKeysLeftHeaders: createColumnKeysLeftHeaders(hasAllowances),
      columnKeysMain: createColumnKeysMain(allowanceIDs, hasAllowances),
      columnKeysMainHeaders: createColumnKeysMainHeaders(allowanceIDs, hasAllowances),
      header: createHeaderCells(allowanceIDs),
      headerProps: createHeaderCellsProps(allowanceIDs, allowanceIDs.length),
      data: data.map(
        ({
          orgID,
          orgTitle,
          parentsExpensesCount,
          budgetExpencesFrom1To11Count,
          budgetExpencesFrom1To11CountTotal,
          budgetExpencesFrom1To4Count,
          totalCount,
        }) => ({
          rowKey: orgID,
          rowData: allowanceIDs.reduce(
            (acc, allowance, index) => {
              const cKey = createDinamicColumnKey(allowance)
              acc[cKey] = { columnKey: cKey, value: String(budgetExpencesFrom1To11Count[index]) }
              return acc
            },
            {
              [ColumnKey.Org]: {
                columnKey: ColumnKey.Org as const,
                value: orgTitle,
              },
              [ColumnKey.ParentsExpenses]: {
                columnKey: ColumnKey.ParentsExpenses as const,
                value: String(parentsExpensesCount),
              },
              [ColumnKey.BudgetExpencesFrom1To11]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To11 as const,
                value: String(budgetExpencesFrom1To11CountTotal),
              },
              [ColumnKey.BudgetExpencesFrom1To11Total]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To11Total as const,
                value: String(budgetExpencesFrom1To11CountTotal),
              },
              [ColumnKey.BudgetExpencesFrom1To4]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To4 as const,
                value: String(budgetExpencesFrom1To4Count),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: String(totalCount),
              },
            },
          ),
        }),
      ),
      dataProps: createCellsProps(allowanceIDs),
      total: [
        {
          rowKey: 'total',
          rowData: allowanceIDs.reduce(
            (acc, allowance, index) => {
              const cKey = createDinamicColumnKey(allowance)
              acc[cKey] = {
                columnKey: cKey,
                value: String(totals.budgetExpencesFrom1To11Count[index]),
              }
              return acc
            },
            {
              [ColumnKey.Org]: {
                columnKey: ColumnKey.Org as const,
                value: 'ИТОГО',
              },
              [ColumnKey.ParentsExpenses]: {
                columnKey: ColumnKey.ParentsExpenses as const,
                value: String(totals.parentsExpensesCount),
              },
              [ColumnKey.BudgetExpencesFrom1To11]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To11 as const,
                value: String(totals.budgetExpencesFrom1To11CountTotal),
              },
              [ColumnKey.BudgetExpencesFrom1To11Total]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To11Total as const,
                value: String(totals.budgetExpencesFrom1To11CountTotal),
              },
              [ColumnKey.BudgetExpencesFrom1To4]: {
                columnKey: ColumnKey.BudgetExpencesFrom1To4 as const,
                value: String(totals.budgetExpencesFrom1To4Count),
              },
              [ColumnKey.Total]: {
                columnKey: ColumnKey.Total as const,
                value: String(totals.totalCount),
              },
            },
          ),
        },
      ],
      totalProps: createTotalCellProps(allowanceIDs),
    }
  }, [allowanceIDs, data, totals])

  return (
    <CompositeTableTemplate
      data={tableData}
      loading={pending}
      renderHeaderCell={headerCell}
      renderCell={renderCellGrayZeros}
      renderFooterCell={renderCellGrayZeros}
      borderTop
    />
  )
}
